import * as React from "react";
import { useTranslation } from "react-i18next";

import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

export interface ToggleFieldProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Static label. When omitted, a translated Visible/Hidden state label is shown. */
  label?: React.ReactNode;
  /** Hide the label entirely (switch only). */
  hideLabel?: boolean;
  disabled?: boolean;
  /** Where the label sits relative to the switch (logical, RTL-aware). */
  labelPosition?: "start" | "end";
  /** `state` = small uppercase muted state text, `field` = regular form label. */
  variant?: "state" | "field";
  size?: "sm" | "md";
  /** Push label and switch to opposite edges. */
  spread?: boolean;
  className?: string;
  labelClassName?: string;
  title?: string;
  id?: string;
}

/**
 * The single shared toggle used across the app: a direction-agnostic switch plus
 * its label. All labelling / spacing / RTL logic lives here — pages should never
 * hand-roll a switch + label row again.
 */
export function ToggleField({
  checked,
  onCheckedChange,
  label,
  hideLabel = false,
  disabled,
  labelPosition = "start",
  variant = "state",
  size = "md",
  spread = false,
  className,
  labelClassName,
  title,
  id,
}: ToggleFieldProps) {
  const { t } = useTranslation();
  const stateText = checked ? t("common.visible") : t("common.hidden");
  const text = label ?? stateText;

  const labelNode = hideLabel ? null : (
    <span
      className={cn(
        "min-w-0 truncate",
        variant === "state"
          ? cn(
              "uppercase tracking-widest text-muted-foreground",
              size === "sm" ? "text-[10px]" : "text-xs",
            )
          : "text-sm font-medium text-foreground",
        labelClassName,
      )}
    >
      {text}
    </span>
  );

  return (
    <label
      className={cn(
        "flex items-center gap-2 cursor-pointer select-none",
        spread && "justify-between w-full",
        disabled && "cursor-not-allowed opacity-60",
        className,
      )}
      title={title ?? (typeof text === "string" ? text : undefined)}
    >
      {labelPosition === "start" && labelNode}
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className="shrink-0"
        aria-label={typeof text === "string" ? text : undefined}
      />
      {labelPosition === "end" && labelNode}
    </label>
  );
}
